// The User Roles dialog's permission toggles (legacy UserPermissionToggles over `GET user-permissions/definitions`):
// the back-office keys in legacy's category order, each category in its order, a child indented under its parent and
// disabled while the parent is off, View Dashboard locked on, deprecated keys never shown, and Floor Plans / QR codes
// only while the location maps tables. Read from the contracts permission catalog.
import { PLATFORM_ONLY_PERMISSIONS, SUPER_ADMIN_ONLY_PERMISSIONS, permissions, settings, type PermissionKey, type SettingKey } from '@radish/contracts';

/** Legacy `BackofficePermissionCategory` order. Categories legacy does not have follow, by name. */
const CATEGORIES = ['Dashboard', 'Reports', 'Items & Menu', 'Discounts', 'Employees', 'Customers', 'Online Store', 'Floor Plans', 'QR Table Pay', 'Order Lookup', 'Settings', 'AI'];

/**
 * View Reports is the register's `view_reports` key, which the back office reads too: it heads Reports here with the
 * back-office registry's label and default (on; the register's is off).
 */
const SHOWN_AS: Partial<Record<PermissionKey, { readonly label: string; readonly category: string; readonly order: number; readonly default: boolean }>> = {
  'reports.view': { label: 'View Reports', category: 'Reports', order: 1, default: true },
  // Legacy's role editor names `manage_employees` "Manage Employees" (ManageEmployees.php); the catalog's label is the
  // permission's wider Next meaning, employees and timecards (B1470).
  'labor.timecards.manage': { label: 'Manage Employees', category: 'Employees', order: 1, default: true },
};

interface Def {
  readonly audience: 'staff' | 'user';
  readonly label: string;
  readonly category: string;
  readonly order: number;
  readonly default: boolean;
  readonly parent?: string;
  readonly gatedBySetting?: string;
  readonly deprecated?: boolean;
  readonly locked?: boolean;
}

interface RoleToggle {
  readonly key: PermissionKey;
  readonly label: string;
  readonly parent: PermissionKey | null;
  readonly locked: boolean;
  /** The legacy location setting that must be on for the toggle to show (legacy `conditional_display`). */
  readonly setting: string | null;
  readonly defaultOn: boolean;
}

interface RoleGroup {
  readonly category: string;
  readonly toggles: readonly RoleToggle[];
}

const rank = (category: string) => {
  const i = CATEGORIES.indexOf(category);
  return i === -1 ? CATEGORIES.length : i;
};

/** The back-office permissions a role can grant, grouped as legacy shows them (staff-only, platform and deprecated keys left out). */
export function rolePermissionGroups(): RoleGroup[] {
  const toggles = (Object.entries(permissions) as [PermissionKey, Def][]).flatMap(([key, d]): (RoleToggle & { category: string; order: number })[] => {
    const shown = SHOWN_AS[key];
    // Users is Super Admin only (A53): legacy listed a Users toggle that opened nothing for a custom role (B289, FIX L433).
    if (SUPER_ADMIN_ONLY_PERMISSIONS.includes(key)) return [];
    if (shown === undefined && (d.audience !== 'user' || PLATFORM_ONLY_PERMISSIONS.includes(key) || d.deprecated === true)) return [];
    return [{
      key, label: shown?.label ?? d.label, category: shown?.category ?? d.category, order: shown?.order ?? d.order,
      parent: (d.parent ?? null) as PermissionKey | null, locked: d.locked === true, setting: d.gatedBySetting ?? null, defaultOn: shown?.default ?? d.default,
    }];
  });
  toggles.sort((a, b) => rank(a.category) - rank(b.category) || a.category.localeCompare(b.category) || a.order - b.order);
  const groups = new Map<string, RoleToggle[]>();
  for (const t of toggles) groups.set(t.category, [...(groups.get(t.category) ?? []), { key: t.key, label: t.label, parent: t.parent, locked: t.locked, setting: t.setting, defaultOn: t.defaultOn }]);
  return [...groups].map(([category, list]) => ({ category, toggles: list }));
}

/** What a new role starts with: legacy's registry defaults (every key on but Use AI Assistant). */
export const roleDefaults = (): PermissionKey[] => rolePermissionGroups().flatMap((g) => g.toggles.filter((t) => t.defaultOn || t.locked).map((t) => t.key));

/** A toggle flipped: a locked key never changes, and turning a parent off turns its children off (legacy updatePermission). */
export function toggled(granted: ReadonlySet<PermissionKey>, key: PermissionKey, on: boolean): Set<PermissionKey> {
  const next = new Set(granted);
  if (on) next.add(key);
  else {
    next.delete(key);
    for (const g of rolePermissionGroups()) for (const t of g.toggles) if (t.parent === key) next.delete(t.key);
  }
  return next;
}

/** A legacy location setting's registry key, or null when the setting is not modelled. */
const registryKeyOf = (legacy: string): SettingKey | null =>
  (Object.entries(settings) as [SettingKey, { readonly legacy: { readonly key: string } | null }][]).find(([, def]) => def.legacy?.key === legacy)?.[0] ?? null;

/** Whether a toggle shows at a location: no condition, or its setting is modelled and on. */
export function roleToggleVisible(t: RoleToggle, isOn: (key: SettingKey) => boolean): boolean {
  if (t.setting === null) return true;
  const key = registryKeyOf(t.setting);
  return key !== null && isOn(key);
}
